import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Modal, Pill, errMsg, money } from "../../comptabilite/_shared";

function EntryRow({ e, checked, onToggle, testId }) {
  return (
    <label className={`flex items-center gap-3 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 ${checked ? "bg-teal-50" : ""}`} data-testid={testId}>
      <input type="checkbox" checked={checked} onChange={onToggle} className="w-4 h-4 accent-teal-600" />
      <span className="w-20 text-slate-600">{e.date}</span>
      <span className="w-14 text-slate-500">{e.no ? `N° ${e.no}` : <span className="text-orange-700">GL att.</span>}</span>
      <span className="w-10 text-slate-500">{e.journal}</span>
      <span className="flex-1 truncate text-slate-800" title={e.memo}>{e.memo}{e.reference ? <span className="text-slate-400"> · {e.reference}</span> : ""}</span>
      <span className={`font-mono font-semibold ${e.amount < 0 ? "text-rose-700" : "text-emerald-700"}`}>{money(e.amount)}</span>
      {e.day_diff !== undefined && <Pill cls={e.day_diff === 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>{e.day_diff === 0 ? "même jour" : `${e.day_diff} j`}</Pill>}
    </label>
  );
}

export default function MatchDialog({ base, line, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`${base}/bank/lines/${line.id}/suggestions`).then((r) => { setData(r.data); if (r.data.exact?.length === 1) setSel(new Set([r.data.exact[0].entry_id])); })
      .catch((e) => { toast.error(errMsg(e)); setData({ exact: [], partial: [] }); });
  }, [base, line.id]);

  const all = useMemo(() => [...(data?.exact || []), ...(data?.partial || [])], [data]);
  const total = useMemo(() => all.filter((e) => sel.has(e.entry_id)).reduce((s, e) => s + e.amount, 0), [all, sel]);
  const balanced = Math.abs(total - line.amount) < 0.005 && sel.size > 0;
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setBusy(true);
    try { await api.post(`${base}/bank/lines/${line.id}/match`, { entry_ids: [...sel] }); toast.success("Ligne conciliée"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Associer à une écriture existante" sub={`${line.date} · ${line.description} · ${money(line.amount)}`} onClose={onClose} wide testId="bank-match-dialog"
      footer={<>
        <span className="text-xs text-slate-500 mr-auto self-center" data-testid="bank-match-total">Sélection : <strong className={`font-mono ${balanced ? "text-emerald-700" : "text-rose-700"}`}>{money(total)}</strong> / {money(line.amount)}</span>
        <Btn tone="slate" onClick={onClose} testId="bank-match-cancel">Annuler</Btn>
        <Btn onClick={save} busy={busy} disabled={!balanced} testId="bank-match-save">Concilier</Btn>
      </>}>
      {data === null ? <div className="p-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
        <>
          <div>
            <div className="text-[11px] font-semibold text-slate-600 mb-1">Écritures au même montant (± 45 jours)</div>
            {data.exact.length === 0 ? <p className="text-xs text-slate-400 border rounded-lg px-3 py-3" data-testid="bank-match-no-exact">Aucune écriture au même montant — utilisez « Comptabiliser » pour créer l'écriture, ou combinez plusieurs écritures ci-dessous.</p>
              : <div className="border rounded-lg divide-y" data-testid="bank-match-exact">{data.exact.map((e) => <EntryRow key={e.entry_id} e={e} checked={sel.has(e.entry_id)} onToggle={() => toggle(e.entry_id)} testId={`bank-match-entry-${e.entry_id}`} />)}</div>}
          </div>
          {data.partial.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold text-slate-600 mb-1">Combiner plusieurs écritures (même sens, ± 7 jours) — le total doit égaler le montant de la ligne</div>
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto" data-testid="bank-match-partial">{data.partial.map((e) => <EntryRow key={e.entry_id} e={e} checked={sel.has(e.entry_id)} onToggle={() => toggle(e.entry_id)} testId={`bank-match-entry-${e.entry_id}`} />)}</div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
